import { Injectable } from '@angular/core'
import snake_case from 'lodash.snakecase'
import { Observable, Subject } from 'rxjs'
import { tap } from 'rxjs/operators'
import { SystemNavigateReason } from 'src/app/interfaces/log'
import {
  MbtiGuestQuizReportDetailResponse,
  MbtiHostInfoResponse,
  MyMbtiHostQuizReportDetailResponse,
} from 'src/app/interfaces/mbti'
import {
  FriendQuizReportPaginatedListResponse,
  GuestQuizReportData,
  GuestQuizReportResponse,
  HostInfo,
  HostQuizReportResponse,
  MyGuestQuizReportPaginatedListResponse,
  MyHostQuizReport,
  MyHostQuizReportPaginatedListResponse,
  QuizReportData,
  QuizReportPostData,
  QuizReportRelationResponse,
  QuizReportType,
  QuizReportUrlType,
} from 'src/app/interfaces/quiz'
import { NavigateParams } from 'src/app/interfaces/router'
import { ProfileTab } from 'src/app/pages/my/my.component'
import { GeneralService } from 'src/app/services/general/general.service'
import { LogService } from 'src/app/services/log/log.service'
import { UserService } from 'src/app/services/user/user.service'
import { environment } from 'src/environments/environment'
import { CustomHttpClient } from '../http/http.service'
import { TEST_GROUP_KEY } from '../log/experiment/experiment.service'
import { customLocalStorage } from './../../../utils/utils'
import { DataBaseService, QuizReportStoreName } from './../database/database.service'
import { RouterService } from './../router/router.service'

const quizApiUrl = `${environment.apiUrl}/v2/question/report`

@Injectable({
  providedIn: 'root',
})
export class QuizService {
  onceAnsweredQuizList: number[] = []
  private _quizReportCreateSubject = new Subject<ProfileTab>()
  private _quizReportDeleteSubject = new Subject<number>()

  private _myQuizReportHideSubject = new Subject<MyHostQuizReport>()
  private _myQuizReportRevealSubject = new Subject<MyHostQuizReport>()
  private _disconnectGuestQuizReportSubject = new Subject<number>()

  private hasMbtiInfo

  constructor(
    private http: CustomHttpClient,
    private db: DataBaseService,
    private userService: UserService,
    private generalSevice: GeneralService,
    private routerService: RouterService,
    private logService: LogService
  ) {}

  getQuizReportCreateSubject(): Observable<ProfileTab> {
    return this._quizReportCreateSubject.asObservable()
  }

  getQuizReportDeleteSubject(): Observable<number> {
    return this._quizReportDeleteSubject.asObservable()
  }

  quizReportDeleted(quizReportId: number): void {
    this._quizReportDeleteSubject.next(quizReportId)
  }

  getMyQuizReportHideSubject(): Observable<MyHostQuizReport> {
    return this._myQuizReportHideSubject.asObservable()
  }

  getMyQuizReportRevealSubject(): Observable<MyHostQuizReport> {
    return this._myQuizReportRevealSubject.asObservable()
  }

  getDisconnectGuestQuizReportSubject(): Observable<number> {
    return this._disconnectGuestQuizReportSubject.asObservable()
  }

  quizReportHided(data: MyHostQuizReport): void {
    this._myQuizReportHideSubject.next(data)
  }

  quizReportRevealed(data: MyHostQuizReport): void {
    this._myQuizReportRevealSubject.next(data)
  }

  disconnectGuestQuizReport(guestQuizReportId: number): void {
    this._disconnectGuestQuizReportSubject.next(guestQuizReportId)
  }

  removeGenericQuizReport(quizReportId: number): Observable<void> {
    return this.http.delete(`${quizApiUrl}/generic/${quizReportId}`, { loggingText: 'remove generic quiz report' })
  }

  /**
   * 퀴즈리포트의 프로필 공개 여부 변경 (isProfileHidden: true <-> false)
   */
  toggleHidingProfile(quizReportId: number): Observable<void> {
    return this.http.post(`${quizApiUrl}/generic/${quizReportId}/hiding`, undefined, {
      loggingText: `toggle private or public quiz report`,
    })
  }

  // 내가 생성한 퀴즈 목록
  getMyHostQuizReportList(
    isProfileHidden: boolean | null,
    cursor: string | null,
    pageSize: number | null = null,
    typeList?: QuizReportType[]
  ): Observable<MyHostQuizReportPaginatedListResponse> {
    const queryParams: any = { cursor, pageSize, isProfileHidden }
    let queryString = typeList?.map((type) => `type=${type}`).join('&')
    Object.keys(queryParams).forEach((key) => {
      const value = queryParams[key]
      if (value !== undefined && value !== null) {
        queryString += `&${snake_case(key)}=${value}`
      }
    })
    return this.http.get<MyHostQuizReportPaginatedListResponse>(`${quizApiUrl}/generic/host/mine?${queryString}`)
  }

  // 내가 답변한 목록
  getMyGuestQuizReportList(
    cursor: string | null,
    pageSize: number | null = null
  ): Observable<MyGuestQuizReportPaginatedListResponse> {
    const queryParams = { cursor, pageSize }
    return this.http.get<MyGuestQuizReportPaginatedListResponse>(`${quizApiUrl}/generic/guest/mine`, { queryParams })
  }

  /** 친구의 공개 퀴즈 목록 */
  getFriendPublicQuizReportList(
    uniqueName: string,
    cursor: string | null,
    pageSize: number | null = null
  ): Observable<FriendQuizReportPaginatedListResponse> {
    const queryParams = { uniqueName, cursor }
    return this.http.get<FriendQuizReportPaginatedListResponse>(`${quizApiUrl}/generic/host/other`, { queryParams })
  }

  // 해당 퀴즈에 내가 한 답변 중 최신 답변.
  getLatestAnswer(hostQuizReport: number): Observable<{ data: { guestQuizReportId: number } }> {
    const queryParams = { hostQuizReport }
    return this.http.get<{ data: { guestQuizReportId: number } }>(`${quizApiUrl}/generic/guest/mine/latest`, {
      queryParams,
    })
  }

  postHostQuizResult(
    body: QuizReportPostData,
    quizReportType: QuizReportUrlType
  ): Observable<HostQuizReportResponse | MyMbtiHostQuizReportDetailResponse> {
    return this.postQuizReport<HostQuizReportResponse | MyMbtiHostQuizReportDetailResponse>(
      body,
      quizReportType,
      'host'
    ).pipe(
      tap((res) => {
        if (this.userService.isLoggedIn) {
          this._quizReportCreateSubject.next('quiz')
        }
      })
    )
  }

  postGuestQuizResult(
    body: GuestQuizReportData,
    quizReportType: QuizReportUrlType
  ): Observable<GuestQuizReportResponse | MbtiGuestQuizReportDetailResponse> {
    return this.postQuizReport<GuestQuizReportResponse | MbtiGuestQuizReportDetailResponse>(
      body,
      quizReportType,
      'guest'
    ).pipe(
      tap((res) => {
        if (this.userService.isLoggedIn) {
          this._quizReportCreateSubject.next('quiz')
        }
      })
    )
  }

  postQuizReport<T>(
    body: QuizReportPostData | GuestQuizReportData,
    quizReportType: QuizReportUrlType,
    userType: 'host' | 'guest'
  ): Observable<T> {
    return this.http.post<T>(`${quizApiUrl}/${quizReportType.toLocaleLowerCase()}/${userType}`, body, {
      loggingText: `create ${quizReportType} quiz report`,
    })
  }

  /** 비회원일 때 작성한 퀴즈리포트 연결 */
  connectDataToLoginUser(): void {
    const guestQuizReportKeys: QuizReportStoreName[] = [
      'mbtiGuestQuizReport',
      'ftGuestQuizReport',
      'attachmentTypeGuestQuizReport',
      'guessMeGuestQuizReport',
      'hspGuestQuizReport',
      'egenTetoGuestQuizReport',
    ]
    const hostQuizReportKeys: QuizReportStoreName[] = [
      'mbtiHostQuizReport',
      'hspHostQuizReport',
      'egenTetoHostQuizReport',
    ]

    const guestQuizReports = guestQuizReportKeys.reduce((acc, key) => {
      const list = this.getAllQuizReportFromLocalStorage(key)
      return acc.concat(list.map((item) => item.id))
    }, [] as number[])

    const hostQuizReports = hostQuizReportKeys.reduce((acc, key) => {
      const list = this.getAllQuizReportFromLocalStorage(key)
      return acc.concat(list.map((item) => item.id))
    }, [] as number[])

    // 저장할게 없는 경우 return;
    if (guestQuizReports.length > 0) {
      const body: { guestQuizReports: number[] } = { guestQuizReports }
      this.http
        .post(`${quizApiUrl}/generic/guest/connect`, body, {
          loggingText: 'connect quiz reports created by anonymous user',
        })
        .subscribe((res) => {
          // connect 성공시, 퀴즈리포트 데이터 제거
          this.clearQuizReportDB(guestQuizReportKeys)
        })
    }
    if (hostQuizReports.length > 0) {
      const body = { hostQuizReports }
      this.http.post(`${quizApiUrl}/generic/host/connect`, body).subscribe((res) => {
        this.clearQuizReportDB(hostQuizReportKeys)
      })
    }
  }

  updateQuizReport(quizReportId: number, data: Partial<QuizReportData>): Observable<void> {
    return this.http.patch(`${quizApiUrl}/generic/${quizReportId}`, data, { loggingText: 'update quiz report' })
  }

  getQuizReportRelation(quizReportId: number, userIdentifier?: string): Observable<QuizReportRelationResponse> {
    const queryParams = {
      userIdentifier: userIdentifier ?? null,
    }
    return this.http.get<QuizReportRelationResponse>(`${quizApiUrl}/generic/${quizReportId}/relation`, { queryParams })
  }

  getHostInformation(quizReportType: QuizReportUrlType, quizReportId: number): Observable<MbtiHostInfoResponse | any> {
    return this.http.get<MbtiHostInfoResponse | any>(`${quizApiUrl}/${quizReportType}/${quizReportId}/host/info`).pipe(
      tap((res: MbtiHostInfoResponse) => {
        // referral 필요할 때만 관련 정보 저장하고 다시 false로 돌려놓기
        if (this.userService.referralUidNeeded && res.data.userId) {
          customLocalStorage.setItem(TEST_GROUP_KEY.REFERRAL_USERID, '' + res.data.userId)
          this.userService.referralUidNeeded = false
          // testGroup -> userProperty log
          this.logService.setUserConfig()
        }
      })
    )
  }

  /** 호스트 퀴즈에 달린 답변을 호스트가 연결을 끊는 api */
  disconnectGuestQuizReportByHost(guestQuizReportId: number): Observable<void> {
    return this.http
      .post<void>(`${quizApiUrl}/generic/${guestQuizReportId}/host/disconnect`)
      .pipe(tap(() => this.disconnectGuestQuizReport(guestQuizReportId)))
  }

  firstName(data?: QuizReportData | HostInfo): string {
    if (data !== undefined) {
      return (data.isProfileHidden ? data.respondentFirstName : data.profile?.firstName) ?? ''
    }
    return ''
  }

  /** 로컬스토리지에 저장된 하나의 QuizReport 가져오기 */
  getQuizReportFromLocalStorage(storeName: QuizReportStoreName, id: number): any | undefined {
    return this.db.getQuizReport(storeName, id)
  }

  /** 로컬스토리지에 저장된 모든 QuizReport 가져오기 */
  getAllQuizReportFromLocalStorage(storeName: QuizReportStoreName): any[] {
    return this.db.getAllQuizReport(storeName)
  }

  getGuestQuizResultUsingHostQuizReportFromStorage(
    storeName: QuizReportStoreName,
    hostQuizReportId: number
  ): any | undefined {
    const list = this.db.getAllQuizReport(storeName)
    let result
    if (!Array.isArray(list)) {
      return
    }
    list.forEach((item) => {
      // 호불호 월드컵은 hostQuizReport가 id만 오고 있음
      if (typeof item.hostQuizReport === 'number') {
        if (item.hostQuizReport === hostQuizReportId) {
          result = item
        }
        return
      }
      // mbti응답의 경우는 hostQuizReport가 Object
      if (item.hostQuizReport?.id === hostQuizReportId) {
        result = item
      }
    })
    return result
  }

  /** 로컬스토리지에 QuizReport 저장하기 */
  setQuizReportToLocalStorage(storeName: QuizReportStoreName, quizReport: any): void {
    this.db.setQuizReport(storeName, quizReport)
  }

  isNameEmpty(name: string | undefined): boolean {
    return name === undefined || name.replace(/[ ]+/g, '') === ''
  }

  /**
   * 퀴즈 리포트 생성 후 redirection 로직
   */
  redirectAfterCreateQuizReport(
    reportType: 'host' | 'guest',
    isProfileHidden: boolean,
    url: string,
    params?: NavigateParams
  ): void {
    const tab = 'quiz'
    this.redirectToQuizReportViaProfile(tab, url, params)
  }

  /** Mbti 생성 history 수 */
  getMbtiCreateUrlCount(): number {
    const routeHistory = this.routerService.getHistory()
    let createUrlCount = 0
    const quizCreateRegExp: RegExp = /(quiz\/create)|(quiz\/\d+)|(fw\/create)|(fw\/\d+)|(fw\/main)/
    for (const route of routeHistory.reverse()) {
      if (quizCreateRegExp.test(route)) {
        createUrlCount = Math.min(createUrlCount + 1, routeHistory.length - 1)
      } else {
        break
      }
    }
    return createUrlCount
  }

  /**
   * 퀴즈 리포트 생성 후 redirection 로직
   *  1. create history 지우기
   *  2. `profile` 페이지로 이동
   *  3. 결과 페이지로 이동
   */
  private redirectToQuizReportViaProfile(tab: ProfileTab, url: string, params?: NavigateParams): void {
    const createUrlCount = this.getMbtiCreateUrlCount()

    if (createUrlCount > 0) {
      history.go(-createUrlCount)
    }

    const navigateProfileTimeout = setTimeout(() => {
      if (this.userService.isLoggedIn) {
        this.routerService.navigate('profile', { queryParams: { tab } }, 'system', {
          reason: SystemNavigateReason.GO_THROUGH_ROUTE,
        })
      } else {
        this.routerService.navigate('')
      }
      const navigateTimeout = setTimeout(() => {
        this.routerService.navigate(url, params)
        clearTimeout(navigateTimeout)
      }, 300)
      clearTimeout(navigateProfileTimeout)
    }, 300)
  }

  private clearQuizReportDB(storageKeys: QuizReportStoreName[]): void {
    storageKeys.forEach((key) => {
      this.db.clearData(key)
    })
  }
}
